import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { trackEvent } from "../../utils/mixpanel";
import { H1, A } from "../../components/Typography";
import Link from "../../components/Link";

interface PortfolioItem {
  id: string;
  image: string;
  description: string;
  alt: string;
}

const portfolioItems: PortfolioItem[] = [
  {
    id: "a",
    image: "/portfolio/a_Full Skækk Screen Fill.gif",
    description: "Concert visual for MC Magnus. Hand-drawn in Procreate",
    alt: "Computer Animation"
  },
  {
    id: "b",
    image: "/portfolio/b_Kolibri.gif",
    description: "Concert visual for MC Magnus. Hand-drawn in Procreate",
    alt: "Kolibri Animation"
  },
  {
    id: "c",
    image: "/portfolio/c_Dart.gif",
    description: "Concert visual for MC Magnus. Hand-drawn in Procreate",
    alt: "Dart Animation"
  },
  {
    id: "d",
    image: "/portfolio/d_Drip.gif",
    description: "Concert visual for MC Magnus. Hand-drawn in Procreate",
    alt: "Drip Simulation"
  },
  {
    id: "e",
    image: "/portfolio/e_8_Ball.gif",
    description: "Concert visual for MC Magnus. Hand-drawn in Procreate",
    alt: "8-Ball Animation"
  },
  {
    id: "f",
    image: "/portfolio/f_hane.gif",
    description: "Concert visual for MC Magnus. Hand-drawn in Procreate",
    alt: "Hane Animation"
  },
  {
    id: "g",
    image: "/portfolio/g_Kangolsnurr.gif",
    description: "Concert visual for MC Magnus. Hand-drawn in Procreate",
    alt: "Kangolsnurr Animation"
  },
  {
    id: "h",
    image: "/portfolio/h_Mc2.gif",
    description: "Concert visual for MC Magnus. Hand-drawn in Procreate",
    alt: "MC2 Animation"
  },
  {
    id: "i",
    image: "/portfolio/i_oslografikk.png",
    description: "Word art for Oslo Grafikk, my own webshop for curated art. Made in Illustrator",
    alt: "Oslo Grafikk word art"
  },
  {
    id: "j",
    image: "/portfolio/j_diff_22.png",
    description: "A 3D reaction-diffusion pattern made with three.js",
    alt: "Differential Equation"
  },
  {
    id: "k",
    image: "/portfolio/k_reaction_diffusion_3D_3.png",
    description: "A 3D reaction-diffusion pattern made with three.js",
    alt: "Reaction Diffusion 3D"
  },
  {
    id: "l",
    image: "/portfolio/l_waveequation.png",
    description: "2D wave equation simulation made with p5.js",
    alt: "2D Wave simulation"
  },
  {
    id: "l2",
    image: "/portfolio/l2_wave3d.mov",
    description: "3D wave equation simulation made with three.js",
    alt: "3D Wave simulation"
  },
  {
    id: "m",
    image: "/portfolio/m_Lysets spektrum grå.png",
    description: "Light spectrum poster made in Sketch",
    alt: "Light Spectrum Poster"
  },
  {
    id: "m2",
    image: "/portfolio/m2_glitchordie.png",
    description: "Logo for a game installation, made in Illustrator",
    alt: "Glitch Art"
  },
  {
    id: "n",
    image: "/portfolio/n_liquified.png",
    description: "Poster made with liquify effects in Photoshop",
    alt: "Liquified Forms"
  },
  {
    id: "o",
    image: "/portfolio/o_transition.png",
    description: "Album cover design for 'Transition' by Shitboy Lee, made in Illustrator",
    alt: "Transition Album Cover"
  },
  {
    id: "o2",
    image: "/portfolio/o2_transition_ShitboyLee.jpg",
    description: "Alternative album cover design, photography + Illustrator",
    alt: "Transition Album Cover Alt"
  },
  {
    id: "p",
    image: "/portfolio/p_sminkepung.png",
    description: "Illustration made in Procreate",
    alt: "Sminke pung Illustration"
  },
  {
    id: "q",
    image: "/portfolio/q_ramen.png",
    description: "Ramen poster, made with hand drawings and Illustrator",
    alt: "Ramen Illustration"
  },
  {
    id: "r",
    image: "/portfolio/r_Estelle.png",
    description: "Poster for my niece, drawn in Procreate",
    alt: "Estelle poster"
  },
  {
    id: "s",
    image: "/portfolio/s_wilma.png",
    description: "Poster for my niece, drawn in Procreate",
    alt: "Wilma poster"
  }
];

const Container = styled.div`
  min-height: 100vh;
  padding: calc(16px + 5vw);
  color: #000000ff;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: calc(32px + 3vw);
`;

const EmailLink = styled(A)`
  font-size: calc(12px + 2vmin);
  text-decoration: none;
  
  &:hover {
    text-decoration: underline;
  }
`;

const MasonryGrid = styled.div`
  display: flex;
  gap: calc(8px + 1vw);
  align-items: flex-start;
`;

const MasonryColumn = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: calc(8px + 1vw);
`;

const PortfolioTile = styled.div`
  width: 100%;
  position: relative;
  overflow: hidden;
  border-radius: 4px;
  cursor: pointer;
`;

const PortfolioImage = styled.img`
  width: 100%;
  height: auto;
  display: block;
  transition: transform 0.3s ease, filter 0.3s ease;
  
  ${PortfolioTile}:hover & {
    transform: scale(1.05);
    filter: brightness(0.3);
  }
`;

const PortfolioVideo = styled.video`
  width: 100%;
  height: auto;
  display: block;
  transition: transform 0.3s ease, filter 0.3s ease;
  
  ${PortfolioTile}:hover & {
    transform: scale(1.05);
    filter: brightness(0.3);
  }
`;

const HoverOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.75);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.3s ease;
  
  ${PortfolioTile}:hover & {
    opacity: 1;
  }
`;

const HoverDescription = styled.p`
  color: white;
  font-size: calc(12px + 1vmin);
  text-align: center;
  padding: calc(8px + 1vw);
  margin: 0;
  line-height: 1.4;
  font-weight: normal;
`;


const Footer = styled.footer`
  padding: calc(16px + 5vw) 0;
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: calc(8px + 1vw);
  }
`;

export default function Creativity() {
  const [columnCount, setColumnCount] = useState(3);

  useEffect(() => {
    trackEvent("Creativity");
  }, []);

  useEffect(() => {
    const updateColumnCount = () => {
      const width = window.innerWidth;
      if (width <= 768) {
        setColumnCount(1);
      } else if (width <= 1024) {
        setColumnCount(2);
      } else if (width >= 1200) {
        setColumnCount(4);
      } else {
        setColumnCount(3);
      }
    };

    updateColumnCount();
    window.addEventListener('resize', updateColumnCount);
    return () => window.removeEventListener('resize', updateColumnCount);
  }, []);

  // Check if file is a video
  const isVideo = (filename: string) => {
    return filename.toLowerCase().includes('.mov') || 
           filename.toLowerCase().includes('.mp4') || 
           filename.toLowerCase().includes('.webm');
  };

  // Distribute items to balance column heights
  const distributeItems = (items: PortfolioItem[], cols: number) => {
    const columns: PortfolioItem[][] = Array.from({ length: cols }, () => []);
    const columnHeights = Array(cols).fill(0);
    
    // Rough height estimates based on common aspect ratios
    const getEstimatedHeight = (filename: string) => {
      const name = filename.toLowerCase();
      
      // Square/portrait items tend to be taller
      if (name.includes('estelle') || name.includes('wilma') || name.includes('spektrum')) {
        return 1.4;
      }
      // Wide landscape items tend to be shorter
      if (name.includes('transition') || name.includes('oslografikk') || name.includes('ramen')) {
        return 0.8;
      }
      // GIFs and videos are often square or slightly tall
      if (name.includes('.gif') || name.includes('.mov')) {
        return 1.2;
      }
      // Default height
      return 1.0;
    };
    
    items.forEach((item) => {
      // Find the column with the smallest height
      const shortestColumnIndex = columnHeights.indexOf(Math.min(...columnHeights));
      
      // Add item to shortest column
      columns[shortestColumnIndex].push(item);
      
      // Add estimated height to column
      const estimatedHeight = getEstimatedHeight(item.image);
      columnHeights[shortestColumnIndex] += estimatedHeight;
    });
    
    return columns;
  };

  const columns = distributeItems(portfolioItems, columnCount);

  return (
    <Container>
      <Header>
        <H1>Stian Surén</H1>
      </Header>
      
      <MasonryGrid>
        {columns.map((columnItems, columnIndex) => (
          <MasonryColumn key={columnIndex}>
            {columnItems.map((item) => (
              <PortfolioTile key={item.id}>
                {isVideo(item.image) ? (
                  <PortfolioVideo 
                    src={item.image} 
                    autoPlay
                    loop
                    muted
                    playsInline
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                    }}
                  />
                ) : (
                  <PortfolioImage 
                    src={item.image} 
                    alt={item.alt}
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                    }}
                  />
                )}
                <HoverOverlay>
                  <HoverDescription>{item.description}</HoverDescription>
                </HoverOverlay>
              </PortfolioTile>
            ))}
          </MasonryColumn>
        ))}
      </MasonryGrid>
       <Footer>
        <Link href="https://linkedin.com/in/stiansuren/">
          Linkedin
        </Link>
        <Link href="https://github.com/stiansuren/">
          Github
        </Link>
        <EmailLink href="mailto:stiansuren@gmail.com">
           stiansuren@gmail.com
        </EmailLink>
      </Footer>
    </Container>
  );
}
